/**
 * ProgressBarModal — auto-update download progress overlay.
 *
 * Listens for Tauri `download-progress` events and renders a
 * non-dismissible modal with an animated progress bar.
 * Opened/closed via the `downloadProgress` flag in `ipcEventsModal` slice.
 */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Modal, { Styles } from "react-modal";
import { isDesktop } from "@platform";

import "./progress-bar-modal.scss";

type UnlistenFn = () => void;

const ProgressBarModal = () => {
  const { downloadProgress } = useSelector(
    ({ ipcEventsModal }) => ipcEventsModal
  );

  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!isDesktop) return;

    let unlisten: UnlistenFn | null = null;

    const setup = async () => {
      const { listen } = await import("@tauri-apps/api/event");
      unlisten = await listen<{ percent: number }>("download-progress", (event) => {
        setWidth(Math.round(event.payload.percent));
      });
    };
    setup();

    return () => {
      unlisten?.();
    };
  }, []);

  return (
    <Modal
      isOpen={downloadProgress}
      shouldCloseOnOverlayClick={false}
      style={customStyles}
    >
      <div className="exit-modal-container">
        <div>
          <h4>Update in progress...</h4>
        </div>
      </div>
      <div className="progress-bar">
        <div className="progress" style={{ width: `${width}%` }}>
          {width > 0 && <span className="progress-label">{`${width}%`}</span>}
        </div>
      </div>
    </Modal>
  );
};

export default ProgressBarModal;

const customStyles: Styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    position: "relative",
    inset: "auto",
    width: "90%",
    maxWidth: "400px",
    border: "1px solid #ccc",
    background: "#fff",
    overflow: "hidden",
    WebkitOverflowScrolling: "touch",
    borderRadius: "8px",
    outline: "none",
    padding: "20px",
  },
};
